import { useState, useCallback } from 'react';
import { api, type AdminAnalyticsUserDetail, type AnalyticsPeriod } from '@/lib/api';
import { PeriodSelector } from './components/PeriodSelector';
import { StatCard } from './components/StatCard';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { BreakdownTable } from './components/BreakdownTable';

interface UsersTabProps {
  initialUserId?: string;
}

export function UsersTab({ initialUserId }: UsersTabProps) {
  const [userIdInput, setUserIdInput] = useState(initialUserId ?? '');
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AdminAnalyticsUserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (overrideUserId?: string, overridePeriod?: AnalyticsPeriod, nocache = false) => {
      const id = (overrideUserId ?? userIdInput).trim();
      const p = overridePeriod ?? period;
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminGetAnalyticsUser(id, p, nocache);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load user detail');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [userIdInput, period],
  );

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') fetchData();
            }}
            placeholder="User ID (usr_...)"
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm w-72"
          />
          <button
            onClick={() => fetchData()}
            disabled={loading || !userIdInput.trim()}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Search'}
          </button>
        </div>
        <PeriodSelector
          period={period}
          onChange={(p) => {
            setPeriod(p);
            // Re-fetch with the new period explicitly — setPeriod is
            // async so the closed-over `period` inside fetchData is
            // still stale at this point.
            if (data) fetchData(data.userId, p);
          }}
          onRefresh={() => data && fetchData(data.userId, undefined, true)}
          loading={loading}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {!data && !error && !loading && (
        <div className="bg-muted/30 border border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
          Search a user_id to view their activity timeline, balance, sessions, and ledger.
          <br />
          <span className="text-xs">
            User IDs can be looked up in the Payments &gt; Customers tab.
          </span>
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Balance"
              value={`$${(data.profile.balance / 100).toFixed(2)}`}
              tone="positive"
              hint={`sub: $${(data.profile.subscriptionCreditBalance / 100).toFixed(2)}`}
            />
            <StatCard
              label="Active Days"
              value={data.summary.activeDaysInPeriod}
              hint={`in ${period}`}
            />
            <StatCard
              label="Est. Active Time"
              value={`${data.summary.estimatedActiveMinutes.toLocaleString()} min`}
              hint="heartbeat-derived"
            />
            <StatCard
              label="Period Spend"
              value={`$${(data.summary.totalSpendCredits / 100).toFixed(2)}`}
              hint={`${data.summary.totalInvocations} invocations`}
              tone="warning"
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Subscription tier"
              value={data.profile.subscription.tier}
              hint={data.profile.subscription.status}
            />
            <StatCard label="Sessions" value={data.summary.totalSessions} />
            <StatCard
              label="Top arche"
              value={data.summary.topArche ?? '—'}
              hint="most opens this period"
            />
            <StatCard
              label="Lifetime spend"
              value={`$${(data.profile.lifetimeUsed / 100).toFixed(2)}`}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Daily Activity</h2>
            <TimeSeriesChart
              data={data.dailyActivity}
              series={[
                { dataKey: 'estimatedActiveMinutes', label: 'Est. min', color: '#22c55e' },
                { dataKey: 'spendCredits', label: 'Spend (credits)', color: '#f59e0b' },
              ]}
              height={180}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Event Timeline</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Last 100 events for this user, most recent first. Shows the user's journey across the
              platform.
            </p>
            <BreakdownTable
              rows={data.eventTimeline}
              emptyMessage="No events recorded for this user in the period."
              columns={[
                {
                  key: 'occurredAt',
                  label: 'When',
                  render: (r) => (
                    <span className="text-xs text-muted-foreground font-mono">
                      {new Date(r.occurredAt).toLocaleString()}
                    </span>
                  ),
                },
                {
                  key: 'eventType',
                  label: 'Type',
                  render: (r) => <span className="text-xs font-mono">{r.eventType}</span>,
                },
                { key: 'eventName', label: 'Name' },
                {
                  key: 'archeId',
                  label: 'Arche',
                  render: (r) =>
                    r.archeId ? <span className="text-xs font-mono">{r.archeId}</span> : '',
                },
                {
                  key: 'pagePath',
                  label: 'Page',
                  render: (r) =>
                    r.pagePath ? (
                      <span className="text-xs text-muted-foreground">{r.pagePath}</span>
                    ) : (
                      ''
                    ),
                },
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">Recent AI Sessions</h2>
              <BreakdownTable
                rows={data.recentSessions}
                emptyMessage="No AI sessions."
                columns={[
                  {
                    key: 'archId',
                    label: 'Arche',
                    render: (r) => <span className="text-xs font-mono">{r.archId}</span>,
                  },
                  { key: 'messageCount', label: 'Msgs', align: 'right' },
                  {
                    key: 'totalCreditsUsed',
                    label: 'Spend',
                    align: 'right',
                    render: (r) => `$${(r.totalCreditsUsed / 100).toFixed(2)}`,
                  },
                  {
                    key: 'lastRequestAt',
                    label: 'Last',
                    render: (r) => (
                      <span className="text-xs text-muted-foreground">
                        {r.lastRequestAt ? new Date(r.lastRequestAt).toLocaleDateString() : '—'}
                      </span>
                    ),
                  },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-3">
                Recent Builder Sessions
              </h2>
              <BreakdownTable
                rows={data.recentBuilderSessions}
                emptyMessage="No builder sessions."
                columns={[
                  {
                    key: 'archId',
                    label: 'Arche',
                    render: (r) => <span className="text-xs font-mono">{r.archId ?? '—'}</span>,
                  },
                  { key: 'status', label: 'Status' },
                  {
                    key: 'totalCostCents',
                    label: 'Cost',
                    align: 'right',
                    render: (r) =>
                      r.totalCostCents ? `$${(r.totalCostCents / 100).toFixed(2)}` : '—',
                  },
                ]}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
